import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, ArrowLeft } from 'lucide-react';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) throw error;
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bt-bg flex flex-col">
      {/* Header */}
      <header
        className="px-4 sm:px-6 py-4"
        style={{ borderBottom: '1px solid var(--bt-glass-stroke)' }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-medium transition-colors"
          style={{ color: 'var(--bt-mute)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--bt-ink)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--bt-mute)')}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Zurück</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md space-y-8">
          {/* Logo + Title */}
          <div className="text-center">
            <div className="inline-flex items-center gap-3 mb-6">
              <div
                style={{
                  width: 44,
                  height: 44,
                  background: 'var(--bt-ink)',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontStyle: 'italic',
                    color: 'var(--bt-ink-2)',
                    fontSize: 22,
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                >
                  m
                </span>
              </div>
              <span
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontStyle: 'italic',
                  fontSize: 24,
                  fontWeight: 500,
                  color: 'var(--bt-ink)',
                  letterSpacing: '-0.02em',
                }}
              >
                mamal
              </span>
            </div>
            <h1
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: 'clamp(26px, 4vw, 36px)',
                fontWeight: 400,
                letterSpacing: '-0.025em',
                color: 'var(--bt-ink)',
                marginBottom: 8,
              }}
            >
              Willkommen zurück
            </h1>
            <p style={{ color: 'var(--bt-mute)', fontSize: 14 }}>
              Melden Sie sich in Ihrem Konto an
            </p>
          </div>

          {/* Card */}
          <div className="bt-glass rounded-2xl p-8">
            {error && (
              <div
                className="mb-6 p-4 rounded-xl text-sm"
                style={{
                  background: 'rgba(239,68,68,0.12)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  color: '#ef4444',
                }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--bt-ink)' }}
                >
                  E-Mail
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4"
                    style={{ color: 'var(--bt-mute)' }}
                  />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3 rounded-xl transition-colors focus:outline-none"
                    style={{
                      background: 'rgba(255,255,255,0.20)',
                      border: '1px solid var(--bt-glass-stroke)',
                      color: 'var(--bt-ink)',
                    }}
                    placeholder="max@beispiel.de"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--bt-ink)' }}
                >
                  Passwort
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4"
                    style={{ color: 'var(--bt-mute)' }}
                  />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3 rounded-xl transition-colors focus:outline-none"
                    style={{
                      background: 'rgba(255,255,255,0.20)',
                      border: '1px solid var(--bt-glass-stroke)',
                      color: 'var(--bt-ink)',
                    }}
                    placeholder="Ihr Passwort"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium transition-colors"
                  style={{ color: 'var(--bt-accent)' }}
                >
                  Passwort vergessen?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--bt-accent)',
                  color: 'white',
                  boxShadow: '0 4px 14px -4px var(--bt-accent-soft)',
                }}
              >
                {loading ? 'Lädt...' : 'Anmelden'}
              </button>
            </form>
          </div>

          <p className="text-center text-sm" style={{ color: 'var(--bt-mute)' }}>
            Noch kein Konto?{' '}
            <Link
              to="/register"
              className="font-medium transition-colors"
              style={{ color: 'var(--bt-accent)' }}
            >
              Registrieren
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
